import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Bottle } from '../types/bottle';
import { BottleCard } from '../components/BottleCard';

interface UserInfo {
  user_id: string;
  email: string;
  store_id: string;
  store_name: string;
  display_name: string;
  role: string;
  store_slug: string;
}

export const BottlesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();
  const [bottles, setBottles] = useState<Bottle[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const startYRef = useRef(0);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * ボトルデータに変更があるかチェック
   * UIに影響する重要フィールドのみ比較
   */
  const hasBottleChanged = (prev: Bottle, current: Bottle): boolean => {
    return (
      prev.analysis_status !== current.analysis_status ||
      prev.updated_at !== current.updated_at ||
      prev.attributes_updated !== current.attributes_updated ||
      prev.is_published !== current.is_published ||
      prev.product_name !== current.product_name ||
      prev.opened_date !== current.opened_date ||
      prev.remaining_status !== current.remaining_status ||
      prev.error_message !== current.error_message
    );
  };

  /**
   * 既存ボトルリストと新規データをマージ
   * 変更のないボトルは同一参照を維持し、再レンダリングを防止
   */
  const mergeBottlesWithDiff = (
    prevBottles: Bottle[],
    newBottles: Bottle[]
  ): Bottle[] => {
    // 新規データをMapに変換（O(1)検索用）
    const newBottlesMap = new Map(
      newBottles.map(bottle => [bottle.bottle_id, bottle])
    );

    // 既存ボトルの更新処理
    const mergedBottles = prevBottles
      .map(prevBottle => {
        const newBottle = newBottlesMap.get(prevBottle.bottle_id);
        
        // 削除されたボトル
        if (!newBottle) {
          return null;
        }
        
        // 変更があった場合のみ新オブジェクトを返す
        if (hasBottleChanged(prevBottle, newBottle)) {
          return newBottle;
        }
        
        // 変更なしは同一参照を維持（再レンダリング回避）
        return prevBottle;
      })
      .filter((bottle): bottle is Bottle => bottle !== null);

    // 既存IDのSet作成
    const existingIds = new Set(prevBottles.map(b => b.bottle_id));

    // 新規追加されたボトルを末尾に追加
    const newlyAddedBottles = newBottles.filter(
      bottle => !existingIds.has(bottle.bottle_id)
    );

    return [...mergedBottles, ...newlyAddedBottles];
  };

  useEffect(() => {
    fetchBottles();
    fetchUserInfo();
    
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.message, location.pathname, navigate]);

  useEffect(() => {
    // データ取得完了後にスクロール位置を復元
    const savedScrollPosition = sessionStorage.getItem('bottlesPageScrollPosition');
    if (savedScrollPosition && bottles.length > 0) {
      const position = parseInt(savedScrollPosition, 10);
      setTimeout(() => {
        window.scrollTo(0, position);
        sessionStorage.removeItem('bottlesPageScrollPosition');
      }, 0);
    }
  }, [bottles]);

  const fetchUserInfo = async () => {
    try {
      setUserInfoLoading(true);
      const idToken = localStorage.getItem('idToken');
      if (!idToken) {
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/users/me`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setUserInfo(data.data);
      }
    } catch (err) {
      console.error('Fetch user info error:', err);
    } finally {
      setUserInfoLoading(false);
    }
  };

  const getRoleLabel = (role: string): string => {
    const roleMap: Record<string, string> = {
      'staff': 'スタッフ',
      'manager': 'マネージャー',
      'owner': 'オーナー',
      'admin': '管理者',
      'service_operator': 'サービス運営者'
    };
    return roleMap[role] || role;
  };

  useEffect(() => {
    // 解析中ボトルがある場合、5秒ごとに自動更新
    const hasPendingBottles = bottles.some(b => b.analysis_status === 'pending');
    
    if (hasPendingBottles) {
      autoRefreshTimerRef.current = setInterval(() => {
        fetchBottles();
      }, 5000);
    } else {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
    }

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [bottles]);

  const fetchBottles = async () => {
    // 初回読み込み判定
    const isInitialLoad = bottles.length === 0;
    
    try {
      // 初回読み込み時のみ loading を true にする
      if (!isRefreshing && isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const idToken = localStorage.getItem('idToken');
      if (!idToken || !user?.store_id) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // 差分更新を適用
        setBottles(prevBottles => {
          // 初回読み込み時は全データをそのまま設定
          if (prevBottles.length === 0) {
            return data.data.bottles;
          }
          
          // 2回目以降は差分マージ
          const mergedBottles = mergeBottlesWithDiff(prevBottles, data.data.bottles);
          
          // 配列の中身を1つずつ比較（参照比較）
          const hasAnyChange = 
            mergedBottles.length !== prevBottles.length ||
            mergedBottles.some((bottle, index) => bottle !== prevBottles[index]);
          
          if (!hasAnyChange) {
            return prevBottles;  // 前回の配列をそのまま返す
          }
          
          return mergedBottles;
        });
      } else {
        setError('ボトル一覧の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch bottles error:', err);
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      // 初回読み込み時のみ loading を false にする
      if (isInitialLoad) {
        setLoading(false);
      }
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchBottles();
  };

  const handleReanalyze = useCallback(async (bottleId: string) => {
    try {
      const idToken = localStorage.getItem('idToken');
      if (!idToken || !user?.store_id) {
        alert('認証情報が見つかりません');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles/${bottleId}/reanalyze`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '再解析に失敗しました');
      }

      // 即座に状態更新
      await fetchBottles();
    } catch (err) {
      console.error('Reanalyze error:', err);
      alert(err instanceof Error ? err.message : '再解析に失敗しました');
    }
  }, [user?.store_id]);

  const handleDelete = useCallback(async (bottleId: string) => {
    if (!confirm('このボトルを削除してもよろしいですか？')) {
      return;
    }

    try {
      const idToken = localStorage.getItem('idToken');
      if (!idToken || !user?.store_id) {
        alert('認証情報が見つかりません');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles/${encodeURIComponent(bottleId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '削除に失敗しました');
      }

      // 即座に状態更新
      await fetchBottles();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err instanceof Error ? err.message : '削除に失敗しました');
    }
  }, [user?.store_id]);

  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    if (currentY - startYRef.current > 100 && window.scrollY === 0 && !isRefreshing) {
      handleRefresh();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAddBottle = () => {
    navigate('/bottles/add');
  };

  const handleBottleClick = useCallback((bottleId: string) => {
    // スクロール位置を保存
    sessionStorage.setItem('bottlesPageScrollPosition', window.scrollY.toString());
    navigate(`/bottles/${encodeURIComponent(bottleId)}/attributes`);
  }, [navigate]);

  return (
    <div 
      className="min-h-screen bg-gray-50"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {/* ハンバーガーメニュー */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 z-50 ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4">
          <button
            onClick={() => setIsMenuOpen(false)}
            className="text-gray-600 hover:text-gray-800 mb-4"
          >
            ← 戻る
          </button>
          
          {userInfoLoading ? (
            <div className="text-center text-gray-600 py-4">読み込み中...</div>
          ) : userInfo ? (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <div className="text-xs text-gray-500">店舗名</div>
                <div className="text-sm font-medium">{userInfo.store_name}</div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-gray-500">ユーザー名</div>
                <div className="text-sm font-medium">{userInfo.display_name}</div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-gray-500">メール</div>
                <div className="text-sm font-medium">{userInfo.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">役割</div>
                <div className="text-sm font-medium">{getRoleLabel(userInfo.role)}</div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-600 py-4">情報を取得できませんでした</div>
          )}

          <div className="border-t pt-4">
            <button
              onClick={() => {
                if (userInfo?.store_slug) {
                  const publicSiteUrl = `${window.location.origin}/website/${userInfo.store_slug}/index.html`;
                  window.open(publicSiteUrl, '_blank');
                }
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg flex items-center gap-2"
            >
              <span>🌐</span>
              <span>一般向け公開サイト</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg flex items-center gap-2 text-red-600"
            >
              <span>🚪</span>
              <span>ログアウト</span>
            </button>
          </div>
        </div>
      </div>

      {/* オーバーレイ */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMenuOpen(true)}
              className="text-2xl text-gray-700 hover:text-gray-900"
            >
              ☰
            </button>
            <h1 className="text-xl font-bold text-gray-900">BottleMan</h1>
          </div>
          <div className="flex gap-3 items-center">
            {user?.role === 'service_operator' && (
              <button
                onClick={() => navigate('/service/dashboard')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                📊 ダッシュボード
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">店舗在庫一覧</h2>
          <button
            onClick={handleAddBottle}
            className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl hover:bg-blue-700 shadow-lg"
          >
            +
          </button>
        </div>

        {isRefreshing && (
          <div className="text-center text-blue-600 mb-4">
            <p className="text-sm">更新中...</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        {loading && !isRefreshing && (
          <div className="text-center text-gray-600 mt-8">
            <p>読み込み中...</p>
          </div>
        )}

        {error && (
          <div className="text-center text-red-600 mt-8">
            <p>{error}</p>
            <button
              onClick={fetchBottles}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              再試行
            </button>
          </div>
        )}

        {!loading && !error && bottles.length === 0 && (
          <div className="text-center text-gray-600 mt-8">
            <p>ボトルが登録されていません</p>
            <p className="mt-2 text-sm">右上の「+」ボタンでボトルを追加できます。</p>
          </div>
        )}

        {!loading && !error && bottles.length > 0 && (
          <div>
            {bottles.map((bottle) => (
              <BottleCard
                key={bottle.bottle_id}
                bottle={bottle}
                onReanalyze={handleReanalyze}
                onDelete={handleDelete}
                onBottleClick={handleBottleClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
